import { useEffect, useState } from "react";
import { ChallengeFormViewModel } from "../ViewModels";
import { AuthService, ChallengeHelper, ChallengeModel } from "@/Domains";
import { ActionResponse, ActionStatusHelper, FeedBackType, ResponseHook, sessionStateActions, ToastHook, useAppNavigation } from "@/Shared";
import { useAppDispatch, useAppSelector } from "@/OneLiners";

/**
 * =====================================================
 * useEnigmaForm
 * =====================================================
 *
 * Form hook handling user challenge (enigma) validation.
 *
 * Responsibilities:
 * - Builds and exposes the ChallengeFormViewModel.
 * - Submits enigma answers to the backend.
 * - Manages submission state (idle, loading, success, error).
 * - Synchronizes challenge state with Redux session.
 * - Handles navigation side-effects based on challenge status.
 *
 * Notes:
 * - All business rules are delegated to ChallengeFormViewModel.
 * - UI feedback is handled exclusively through ToastHook.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export const useEnigmaForm = (toastHook: ToastHook, clearDigits: () => void) => {

    const { goToLogin, goToGoogle } = useAppNavigation();


     /**
     * =====================================================
     * Redux & Local state
     * =====================================================
     *
     * - dispatch: redux dispatcher
     * - userChallenge: current challenge stored in session
     * - enigmaResponse: local state tracking submission status
     */
    const dispatch = useAppDispatch();
    const userChallenge = useAppSelector(state => state.sessionState.userChallenge);
    const [enigmaResponse, setEnigmaResponse] = useState<ActionResponse>({ status: 'idle' });

    const [entityFormVM, setFormVM] = useState<ChallengeFormViewModel>();

    
     /**
     * =====================================================
     * Effect: handle challenge response
     * =====================================================
     *
     * - Success: show success toast and redirect to login
     * - Failure: show error toast and clear input
     * - Idle but validated: navigate to login
     * - Blocked: show blocked toast and redirect to Google
     */
    useEffect(() => {
        if (userChallenge) {
            if (ChallengeHelper.isValidated(userChallenge)) {
                goToLogin();
                return;
            }
            setFormVM(new ChallengeFormViewModel(userChallenge));
        }
    }, [userChallenge]);


     /**
     * =====================================================
     * Form submission handler
     * =====================================================
     *
     * handleEnigma:
     * - Sends enigma answers to backend via AuthService
     * - Updates response state
     * - Updates session state with latest challenge
     * - Handles blocked challenge (HTTP 423)
     */
    useEffect(() => {

        clearDigits();
        if (!entityFormVM) return;
        
        const {isSucceeded, isFailed, isIdle} = ActionStatusHelper.is(enigmaResponse.status);
        
        if (isSucceeded) {
            toastHook.openToast({
                data: {
                    message: entityFormVM.getSuccess(),
                    type: FeedBackType.SUCCESS
                },
                onClose: () => {
                    clearDigits();
                    goToLogin();
                }
            });
            return;
        }
        
        if (isFailed) {
            toastHook.openToast({
                data: {
                    message: entityFormVM.getError(enigmaResponse.error),
                    type: FeedBackType.ERROR
                },
                onClose: clearDigits
            });
            return;
        }

        if (isIdle && entityFormVM.isValidated()) {
            goToLogin();
            return;
        }

        if (entityFormVM.isBlocked()) {
            toastHook.openToast({
                data: {
                    message: entityFormVM.getError("Vous n'avez pas réussi à prouver que vous êtes un humain. Veuillez contacter l'administrateur du site."),
                    type: FeedBackType.ERROR
                },
                onClose: goToGoogle
            });
        }

    }, [enigmaResponse.status, entityFormVM]);

    /**
     * =====================================================
     * Form Submission Handler
     * =====================================================
     *
     * handleEnigma:
     * - Submits enigma values to backend via AuthService
     * - Updates response state (loading, success, error)
     * - Updates Redux session state with new challenge data
     * - Handles blocked challenge scenario (HTTP 423)
     */
    const handleEnigma = (id: number, values: any) => {
        ResponseHook.loading(setEnigmaResponse);
        AuthService.validateEnigma(id, values).then(data => {

            ResponseHook.success(setEnigmaResponse);
            const updatedChallenge = data.data;

            // Update user challenge in session state
            dispatch(sessionStateActions.setUserChallenge(updatedChallenge));

        }).catch(error => {
            ResponseHook.error(setEnigmaResponse, error, "Erreur lors de la soumission de l'énigme.");
            const code = error.response?.status;

            // Handle specific case where challenge is blocked (HTTP 423)
            if (code === 423) {
                dispatch(
                    sessionStateActions.setUserChallenge(
                        {
                            ...userChallenge,
                            blockedAt: new Date()
                        } as ChallengeModel
                    )
                );
            }
        });

    };

    /**
     * =====================================================
     * Hook API
     * =====================================================
     *
     * Exposed functions and state:
     * - handleEnigma: direct submission handler
     * - formVM: memoized ChallengeFormViewModel
     * - enigmaResponse: current submission status
     */
    return {
        entityFormVM,
        enigmaResponse,
        handleEnigma
    }
}